import { useId, type ComponentPropsWithRef } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import {
  Beta,
  IconArrowLeftDefault,
  IconArrowRightDefault,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { BREAKPOINTS, useMediaQuery } from '@mc/media'
import { TrackClick } from 'pages/certificates/tracking'
import type { CohortContent, CohortCurriculumWeek } from '../types'
import { CohortSectionHeader } from './CohortSectionHeader'
import { useScrollDrivenTrack } from './useScrollDrivenTrack'

/** `CarouselItem` passes its ref, tabIndex and aria-hidden to this element. */
const WeekCard = ({
  week: { label, title, description, outcomesHeading, outcomes },
  ...carouselItemProps
}: ComponentPropsWithRef<'article'> & {
  week: CohortCurriculumWeek
}) => (
  <article
    {...carouselItemProps}
    className='flex w-11/12 shrink-0 flex-col rounded-xl bg-c-beige-1300 p-6 text-c-text-light lg:w-5/12 lg:p-7'
  >
    <p className='c-d-1 m-0 text-c-brand-highlight'>{label}</p>
    <hr className='my-6 border-0 border-t border-dotted border-c-base-900' />
    <h3 className='c-heading-3 m-0'>{title}</h3>
    <p className='c-body mb-0 mt-4'>{description}</p>
    <p className='c-caption-mono mb-4 mt-7 text-c-brand-highlight'>
      {outcomesHeading}
    </p>
    <ul className='m-0 flex list-none flex-col gap-3 p-0'>
      {outcomes.map((outcome) => (
        <li key={outcome} className='c-body flex items-start gap-3'>
          <span
            aria-hidden
            className='mt-2 size-2 shrink-0 bg-c-brand-highlight'
          />
          {outcome}
        </li>
      ))}
    </ul>
  </article>
)

const WeeksCarousel = ({ weeks }: { weeks: CohortCurriculumWeek[] }) => {
  const { t } = useTranslation('@mc/certificates')

  return (
    <div className='mt-8'>
      <Beta.CarouselV2.Carousel
        srLabel={t('marketingPages.cohorts.curriculum.carouselLabel')}
        scrollCount={1}
      >
        <Beta.CarouselV2.CarouselContent
          overflow
          scrollSnapAlign='start'
          className='items-stretch gap-5'
        >
          {weeks.map((week) => (
            <Beta.CarouselV2.CarouselItem key={week.label}>
              <WeekCard week={week} />
            </Beta.CarouselV2.CarouselItem>
          ))}
        </Beta.CarouselV2.CarouselContent>

        <div className='sr-only mt-6 flex items-center justify-center gap-2 lg:not-sr-only'>
          <TrackClick event='certificateRowScrolled' direction='left'>
            <Beta.CarouselV2.CarouselPrevious
              kind='link'
              size='md'
              symmetrical
              className='p-4'
            >
              <IconArrowLeftDefault size='md' />
            </Beta.CarouselV2.CarouselPrevious>
          </TrackClick>
          <TrackClick event='certificateRowScrolled' direction='right'>
            <Beta.CarouselV2.CarouselNext
              kind='link'
              size='md'
              symmetrical
              className='p-4'
            >
              <IconArrowRightDefault size='md' />
            </Beta.CarouselV2.CarouselNext>
          </TrackClick>
        </div>
      </Beta.CarouselV2.Carousel>
    </div>
  )
}

const CurriculumFooter = ({ disclaimer }: { disclaimer: string }) => (
  <div className='mt-7 flex flex-col items-center gap-4 text-center'>
    <hr className='m-0 w-11 border-0 border-t border-dotted border-c-base-900' />
    <p className='c-body-small m-0 text-c-text-tint-3'>{disclaimer}</p>
  </div>
)

const ScrollDrivenWeeks = ({
  curriculum,
  headingId,
}: {
  curriculum: CohortContent['curriculum']
  headingId: string
}) => {
  const { sectionRef, stickyRef, trackRef, sectionHeight, stickyTop, x } =
    useScrollDrivenTrack()

  return (
    <div
      ref={sectionRef}
      data-testid='cohort-curriculum-scroll-driven'
      style={{ height: sectionHeight || undefined }}
    >
      <div
        ref={stickyRef}
        className='container sticky flex min-h-screen flex-col justify-center py-6'
        style={{ top: stickyTop }}
      >
        <CohortSectionHeader
          id={headingId}
          eyebrow={curriculum.eyebrow}
          headline={curriculum.headline}
        />
        <motion.div ref={trackRef} className='mt-8 flex gap-5' style={{ x }}>
          {curriculum.weeks.map((week) => (
            <WeekCard key={week.label} week={week} />
          ))}
        </motion.div>
        <CurriculumFooter disclaimer={curriculum.disclaimer} />
      </div>
    </div>
  )
}

export const CohortCurriculum = ({
  curriculum,
}: {
  curriculum: CohortContent['curriculum']
}) => {
  const headingId = useId()
  // Inverted on purpose: useMediaQuery starts true, which keeps SSR and first paint on the carousel
  const isBelowDesktop = useMediaQuery(`(max-width: ${BREAKPOINTS.LG - 1}px)`)
  const prefersReducedMotion = useReducedMotion()
  const isScrollDriven = !isBelowDesktop && !prefersReducedMotion

  return (
    <section aria-labelledby={headingId} className='overflow-x-clip'>
      {isScrollDriven ? (
        <ScrollDrivenWeeks curriculum={curriculum} headingId={headingId} />
      ) : (
        <div className='container py-8'>
          <CohortSectionHeader
            id={headingId}
            eyebrow={curriculum.eyebrow}
            headline={curriculum.headline}
          />
          <WeeksCarousel weeks={curriculum.weeks} />
          <CurriculumFooter disclaimer={curriculum.disclaimer} />
        </div>
      )}
    </section>
  )
}
